// @ts-nocheck
import React from "react";
import { ReactComponent as BriefcaseIcon } from "../../assets/icons/briefcase-02.svg";
import { ReactComponent as GlobeIcon } from "../../assets/icons/globe-03.svg";
import { ReactComponent as CompassIcon } from "../../assets/icons/compass-03.svg";

interface PersonaDetailsModalProps {
  persona: {
    id: string;
    name: string;
    selectedNeeds?: string[];
    regions?: string[];
    markets?: string[];
    description?: string;
    summary?: string;
    completed?: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
  onClose: () => void;
}

const PersonaDetailsModal: React.FC<PersonaDetailsModalProps> = ({ persona, onClose }) => {
  const parseSummary = (summaryStr: string | null | undefined) => {
    if (!summaryStr) return null;
    try {
      return JSON.parse(summaryStr);
    } catch {
      return null;
    }
  };

  const summary = parseSummary(persona?.summary);
  
  // Define the categories for proper color coding
  const WORK_AREAS = ["Trading", "Operations", "Asset Management", "Market Analysis", "Risk", "Other"];
  const ASSETS = ["Hydro", "Wind", "Solar", "Battery", "Thermal", "Load"];
  
  // Separate work areas and assets from selectedNeeds
  const workAreas = persona?.selectedNeeds?.filter(item => WORK_AREAS.includes(item)) || [];
  const assets = persona?.selectedNeeds?.filter(item => ASSETS.includes(item)) || [];
  
  const objectiveLabel = persona?.description || "Not specified";

  return (
    <div className="p-2">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Persona Details</h2>
        <button
          onClick={() => onClose()}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-4">
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-500 mb-1">Name</h4>
          <p className="text-gray-900 font-semibold">{persona?.name || "Untitled"}</p>
        </div>

        {/* Three main sections matching the onboarding summary */}
        <div className="space-y-4">
          {/* What you do section with tags */}
          <div className="bg-[rgba(162,187,191,0.15)] rounded-xl px-4 py-4">
            <div className="flex items-start gap-3">
              <div>
                <BriefcaseIcon className="w-5 h-5 text-eai-primary-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-2">What you do</p>
                {workAreas.length > 0 || assets.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {workAreas.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-3 py-1 bg-green-50 rounded-full text-sm font-medium text-green-700 border border-green-200"
                      >
                        {tag}
                      </span>
                    ))}
                    {assets.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-3 py-1 bg-blue-50 rounded-full text-sm font-medium text-blue-700 border border-blue-200"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-base text-gray-400">Not specified</p>
                )}
              </div>
            </div>
          </div>

          {/* Where you're focused section */}
          <div className="bg-[rgba(63,131,248,0.1)] rounded-xl px-4 py-4">
            <div className="flex items-start gap-3">
              <div>
                <GlobeIcon className="w-5 h-5 text-[#3f83f8]" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-2">Where you're focused</p>
                {(persona?.regions && persona.regions.length > 0) || (persona?.markets && persona.markets.length > 0) ? (
                  <div className="flex flex-wrap gap-2">
                    {persona?.regions?.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-3 py-1 bg-orange-50 rounded-full text-sm font-medium text-orange-700 border border-orange-200"
                      >
                        {tag}
                      </span>
                    ))}
                    {persona?.markets?.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-3 py-1 bg-primary-50 rounded-full text-sm font-medium text-primary-700 border border-primary-200"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-base text-gray-400">Any location</p>
                )}
              </div>
            </div>
          </div>

          {/* Your objective section */}
          <div className="bg-[#f8fbea] rounded-xl px-4 py-4">
            <div className="flex items-start gap-3">
              <div>
                <CompassIcon className="w-5 h-5 text-eai-primary-accent" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-2">Your objective</p>
                <p className="text-base font-medium text-eai-dark">
                  {objectiveLabel}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Recommendations section if available */}
        {summary?.recommendations && summary.recommendations.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Recommendations</h4>
            <ul className="space-y-2">
              {summary.recommendations.map((item: string, index: number) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <span className="mt-0.5 text-eai-primary-accent">●</span>
                  <span className="text-eai-dark">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Metadata section */}
        <div className="border-t pt-4 mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Status</span>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                persona?.completed
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {persona?.completed ? "Completed" : "In Progress"}
            </span>
          </div>
          
          {persona?.createdAt && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Created</span>
              <span className="text-sm text-gray-900">
                {new Date(persona.createdAt).toLocaleDateString()}
              </span>
            </div>
          )}
          
          {persona?.updatedAt && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Last Updated</span>
              <span className="text-sm text-gray-900">
                {new Date(persona.updatedAt).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={() => onClose()}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default PersonaDetailsModal;